import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { salesOrderSchema, type SalesOrderFormData, type SalesOrder } from '../../types/salesOrder';
import { inventoryService } from '../../services/inventoryService';
import { inventoryLotsService } from '../../services/inventory/inventoryLotsService';
import { finishedGoodsService } from '../../services/inventory/finishedGoodsService';
import { customerService } from '../../services/customerService';
import { useSettingsStore } from '../../store/settingsStore';
import { useAuthStore } from '../../store/authStore';
import type { InventoryItem, InventoryLot } from '../../types/inventory';
import type { Customer, CustomerFormData } from '../../types/customer';
import type { AutocompleteOption } from '../../components/common/AutocompleteSelect';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { useOnboardingStore } from '../../store/onboardingStore';
import { type WeightUnit } from '../../hooks/useWeightUnit';

interface Props {
  initialData?: SalesOrderFormData;
  isOpen?: boolean;
  onSubmit: (data: SalesOrderFormData) => Promise<void>;
  onCancel: () => void;
  /** Viewer mode: no field may be edited, because nothing can be saved. */
  readOnly?: boolean;
}

// One pickable lot for a GB SO line. Sorted ASC by receivedDate (oldest first)
// so the array's index 0 is the FIFO default.
export interface GbLotOption {
  id: string;
  lotNumber: string;
  processType: string;
  remaining: number;
  unitPrice: number;
  receivedDate: string;
}

export const useSalesOrderForm = ({ initialData, isOpen = true, onSubmit, onCancel, readOnly = false }: Props) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('sales_orders', 'write');
  const { currencySymbol } = useSettingsStore();
  const { company, user } = useAuthStore();
  // The sample-only pickers belong to the guided sale (step 6) and to nothing else.
  // Gating them on "a tour is running" hid the user's own customers and finished
  // goods on every other step, so a real sales order raised mid-tour could not find
  // what it was selling.
  const onboardingSamplePickers = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 6,
  );
  // While the tour runs, restrict the pickers to the tagged sample data only —
  // the sample finished good carries is_onboarding_sample; the sample customer id
  // lives in sampleRefs.
  const sampleRefs = useOnboardingStore((s) => s.state?.sampleRefs);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [gbLotsByItem, setGbLotsByItem] = useState<Record<string, GbLotOption[]>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerOptions, setCustomerOptions] = useState<AutocompleteOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(Boolean(isOpen));
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isCancelWarningOpen, setIsCancelWarningOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<SalesOrderFormData | null>(null);
  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);
  const [createCustomerInitialName, setCreateCustomerInitialName] = useState<string | undefined>(undefined);
  const [displayUnit, setDisplayUnit] = useState<WeightUnit>('kg');

  const { register, control, handleSubmit, watch, setValue, setError, formState: { errors, isSubmitting } } = useForm<SalesOrderFormData>({
    resolver: zodResolver(salesOrderSchema),
    defaultValues: initialData || {
      customer: '',
      customerEmail: '',
      emailCc: '',
      emailBcc: '',
      sendLater: false,
      orderDate: new Date().toISOString().split('T')[0],
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      expectedShipDate: '',
      billingAddress: '',
      shippingAddress: '',
      terms: '',
      shipVia: '',
      shippingDate: '',
      trackingNumber: '',
      businessDevelopmentExecutive: '',
      tags: '',
      store: '',
      status: 'Draft',
      paymentStatus: 'unpaid',
      internalNotes: '',
      messageOnInvoice: '',
      discountType: 'amount',
      discountValue: 0,
      shippingAmount: 0,
      items: [{ inventoryItemId: '', itemType: 'finished_good', roastType: '', lotId: '', lotNumber: '', processType: '', quantity: 1, unitPrice: 0, name: '', description: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchedItems = watch('items');
  const shippingAmount = watch('shippingAmount') || 0;
  const discountType = watch('discountType') || 'amount';
  const discountValue = watch('discountValue') || 0;
  const invoiceDate = watch('invoiceDate');
  const terms = watch('terms');
  const currentStatus = watch('status');
  const shippingAddress = watch('shippingAddress') || '';

  const subtotalAmount = watchedItems?.reduce((sum, item) => {
    return sum + ((item.quantity || 0) * (item.unitPrice || 0));
  }, 0) || 0;

  // Resolve the order-level discount to a concrete Rp deduction. Percent mode
  // is capped at 100%, and the deduction can never exceed the subtotal so the
  // total stays >= shipping. Mirrors normalizeDiscount() on the backend.
  const discountAmount = (() => {
    const raw = discountType === 'percent'
      ? (subtotalAmount * Math.min(discountValue, 100)) / 100
      : discountValue;
    return Math.min(Math.max(raw, 0), subtotalAmount);
  })();

  const totalAmount = subtotalAmount - discountAmount + (shippingAmount || 0);
  const paymentStatus = watch('paymentStatus') || 'unpaid';
  // There are no partial payments (spec 100), so the balance is all-or-nothing.
  const balanceDue = paymentStatus === 'paid' ? 0 : totalAmount;

  const loadData = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const [invData, custData, rbGroups, gbLotsResp, fgProductions] = await Promise.all([
        inventoryService.getAll({ includeOnboardingSamples: onboardingSamplePickers }),
        customerService.getAll({ includeOnboardingSamples: onboardingSamplePickers }),
        inventoryService.getRoastedBulkGroups(false, { status: 'active' }),
        // Pre-fetch all active GB lots in one call so the lot-picker UI is
        // synchronous. Limit is generous; production sets cap at this batch.
        inventoryLotsService.getAllLots({ item_type: 'green_bean', status: 'active', limit: 5000 }),
        // FG productions are needed to exclude FGs whose only existence is a
        // draft packing process — those should not appear in the SO picker
        // until the production is completed (WO-022).
        finishedGoodsService.listFinishedGoodsProductions({ status: 'all' }),
      ]);

      // An FG inventory_item row is created the moment a packing process draft
      // is saved (for the "new FG" flow), but the FG hasn't actually been
      // produced yet. Hide such FGs from the SO picker until at least one of
      // their fg_productions reaches 'completed'.
      const fgWithCompleted = new Set<string>();
      const fgWithDraft = new Set<string>();
      for (const p of fgProductions) {
        if (p.status === 'completed') fgWithCompleted.add(p.fg_id);
        else if (p.status === 'draft') fgWithDraft.add(p.fg_id);
      }
      const draftOnlyFgIds = new Set<string>();
      for (const id of fgWithDraft) {
        if (!fgWithCompleted.has(id)) draftOnlyFgIds.add(id);
      }
      const filteredInvData = invData.filter(
        (i) => !(i.itemType === 'finished_good' && draftOnlyFgIds.has(i.id))
      );

      // Replace per-physical RB inventory items with synthesized group rows so
      // the picker shows aggregated remaining stock per (item_id, roast_type)
      // group. FIFO across the underlying physical RB rows happens server-side
      // at submit time (DeductRoastedBulkGroupFIFO).
      const nonRb = filteredInvData.filter(i => i.itemType !== 'roasted_bulk');
      const titleCaseRoast = (rt: string) =>
        rt ? rt.charAt(0).toUpperCase() + rt.slice(1) : '';
      const syntheticRb: InventoryItem[] = rbGroups.map(g => ({
        id: `${g.itemId}:${g.roastType}`,
        code: g.itemCode,
        // Bake the roast variant into the displayed name so the picker option,
        // the form's "name" field, and the description column all reflect the
        // (item, roast) pair the line targets.
        name: g.roastType ? `${g.itemName} - ${titleCaseRoast(g.roastType)}` : g.itemName,
        description: '',
        unitOfMeasure: 'kg',
        itemType: 'roasted_bulk',
        status: 'active',
        reorderPoint: 0,
        quantity: g.totalStock,
        stock: g.totalStock,
        standardCost: g.averagePrice,
        averagePrice: g.averagePrice,
      }));
      const merged = [...nonRb, ...syntheticRb];
      setInventoryItems(onboardingSamplePickers ? merged.filter(i => i.isOnboardingSample) : merged);

      // Group GB lots by inventory_item_id and enrich each lot with its
      // process_type pulled from the parent item's greenBeanProfiles
      // (where type='LotType' && lot_id matches). Lots are sorted ASC by
      // received_date — index 0 is the FIFO default.
      const gbItemsById = new Map(merged.filter(i => i.itemType === 'green_bean').map(i => [i.id, i]));
      const lotsByItem: Record<string, GbLotOption[]> = {};
      for (const lot of gbLotsResp.data as InventoryLot[]) {
        if (!lot.inventoryItemId || lot.remainingQuantity <= 0.0001) continue;
        const parent = gbItemsById.get(lot.inventoryItemId);
        const profile = parent?.greenBeanProfiles?.find(p => p.lot_id === lot.id && p.type === 'LotType');
        const opt: GbLotOption = {
          id: lot.id,
          lotNumber: lot.lotNumber,
          processType: profile?.process_type || '',
          remaining: lot.remainingQuantity,
          unitPrice: lot.unitPrice ?? 0,
          receivedDate: lot.receivedDate,
        };
        (lotsByItem[lot.inventoryItemId] ||= []).push(opt);
      }
      for (const itemId of Object.keys(lotsByItem)) {
        lotsByItem[itemId].sort((a, b) => {
          if (a.receivedDate === b.receivedDate) return a.lotNumber.localeCompare(b.lotNumber);
          return a.receivedDate.localeCompare(b.receivedDate);
        });
      }
      setGbLotsByItem(lotsByItem);

      const visibleCustomers = onboardingSamplePickers ? custData.filter(c => c.id === sampleRefs?.customerId) : custData;
      setCustomers(visibleCustomers);
      setCustomerOptions(visibleCustomers.map(c => ({
        id: c.id,
        label: c.name,
        text: c.name
      })));

      if (initialData) {
        initialData.items.forEach((line, index) => {
          const inv = merged.find(i => i.id === line.inventoryItemId);
          setValue(`items.${index}.itemType`, inv?.itemType ?? 'finished_good');
          // Sync display name with the synthetic option name so RB lines show
          // the roast suffix even when the backend item_name was the base GB.
          if (inv) {
            setValue(`items.${index}.name`, inv.name);
          }
        });
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setIsDataLoading(false);
    }
  }, [initialData, setValue, onboardingSamplePickers, sampleRefs]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    loadData();
  }, [isOpen, loadData]);

  useEffect(() => {
    if (!invoiceDate || !terms) {
      return;
    }

    let days = 0;
    if (terms === 'Net 7') {
      days = 7;
    } else if (terms === 'Net 14') {
      days = 14;
    } else if (terms === 'Net 30') {
      days = 30;
    } else if (terms === 'Due on receipt') {
      days = 0;
    } else {
      return;
    }

    const date = new Date(invoiceDate);
    if (Number.isNaN(date.getTime())) {
      return;
    }
    date.setDate(date.getDate() + days);
    const formatted = date.toISOString().split('T')[0];
    setValue('dueDate', formatted);
  }, [invoiceDate, terms, setValue]);

  const openCreateCustomerModal = (name: string) => {
    setCreateCustomerInitialName(name);
    setIsCreateCustomerModalOpen(true);
  };

  const closeCreateCustomerModal = () => {
    setIsCreateCustomerModalOpen(false);
    setCreateCustomerInitialName(undefined);
  };

  const handleCreateCustomer = async (data: CustomerFormData) => {
    const created = await customerService.create(data);
    setCustomers((prev) => [...prev, created]);
    setCustomerOptions((prev) => [
      ...prev,
      { id: created.id, label: created.name, text: created.name }
    ]);

    setValue('customer', created.name, { shouldValidate: true, shouldDirty: true });
    setValue('customerEmail', created.email || '', { shouldValidate: true, shouldDirty: true });
    setValue('billingAddress', created.billingAddress || '', { shouldValidate: true, shouldDirty: true });
    setValue('shippingAddress', created.shippingAddress || created.billingAddress || '', { shouldValidate: true, shouldDirty: true });
    setValue('messageOnInvoice', created.messageOnInvoice || '', { shouldValidate: true, shouldDirty: true });

    closeCreateCustomerModal();
  };

  const isInitialDelivered = Boolean(initialData && 'status' in initialData && (initialData as SalesOrder).status === 'Delivered');
  const isInitialCancelled = Boolean(initialData && 'status' in initialData && (initialData as SalesOrder).status === 'Cancelled');
  const isEditMode = Boolean(initialData && 'id' in initialData);
  const enforceQuantityLimit = true;

  // Reservations the SO was already holding at load time. When editing a draft/sent
  // SO, the displayed `stock` (= on_hand - reserved) already excludes this SO's own
  // reservation; we add it back so the user can edit lines up to (free + self-held).
  // For a brand-new SO or one that wasn't reserving (delivered/cancelled), this map
  // is empty.
  // Live statuses physically hold lot stock under the reservation-as-decrement
  // model (draft/sent/delivered all decrement inventory_lots.remaining_quantity
  // for GB). The form must add the SO's own consumption back into visible stock
  // so the user can edit their own line up to the gross pool, not just what's
  // free for other orders.
  const initialReserving =
    initialData?.status === 'Draft' ||
    initialData?.status === 'Sent' ||
    initialData?.status === 'Delivered';
  const ownReservedByItem = useMemo<Record<string, number>>(() => {
    if (!initialReserving || !initialData?.items) {
      return {};
    }
    const map: Record<string, number> = {};
    for (const line of initialData.items) {
      if (!line.inventoryItemId) {
        continue;
      }
      map[line.inventoryItemId] = (map[line.inventoryItemId] || 0) + (line.quantity || 0);
    }
    return map;
  }, [initialReserving, initialData?.items]);
  const areFieldsDisabled = Boolean(
    !canWrite || readOnly || (isEditMode && (currentStatus === 'Cancelled' || currentStatus === 'Delivered'))
  );
  const isStatusDisabled = Boolean(!canWrite || readOnly || isInitialCancelled);
  // Payment status is deliberately exempt from the Delivered/Cancelled freeze
  // above. Cash-basis revenue is recognised when money arrives, and money
  // usually arrives *after* delivery — freezing the field on delivery would
  // lock an order out of ever being marked paid from the form, which is the
  // one edit a delivered order most needs. Writing it is safe: the PUT touches
  // sales_orders and rebuilds sales_order_items (nothing references those
  // rows), and never moves stock — stock transitions live on the separate
  // status endpoint.
  const isPaymentStatusDisabled = Boolean(!canWrite || readOnly);
  const restrictStatusOptions = Boolean(isInitialDelivered);

  const quantityLimitErrors = watchedItems?.reduce<Record<number, string>>((acc, item, index) => {
    if (!enforceQuantityLimit || !item?.inventoryItemId) {
      return acc;
    }

    const selectedItem = inventoryItems.find((inventoryItem) => inventoryItem.id === item.inventoryItemId);
    if (!selectedItem) {
      return acc;
    }

    const requestedQuantity = Number(item.quantity || 0);
    const ownReserved = ownReservedByItem[item.inventoryItemId] || 0;
    const remainingQuantity = Number(selectedItem.stock || 0) + ownReserved;

    if (requestedQuantity > remainingQuantity) {
      acc[index] = t('salesOrder.items.errors.quantityExceedsRemainingLimit');
    }

    return acc;
  }, {}) || {};

  const hasQuantityLimitError = Object.keys(quantityLimitErrors).length > 0;
  const hasInvalidUnitPriceError =
    watchedItems?.some((item) => item?.inventoryItemId && Number(item.unitPrice || 0) <= 0) ?? false;
  const isShippingAddressMissing = !shippingAddress || shippingAddress.trim().length === 0;

  const processSubmit = async (data: SalesOrderFormData) => {
    setLoading(true);
    setIsError(false);
    try {
      const enrichedItems = data.items.map(item => {
        const invItem = inventoryItems.find(i => i.id === item.inventoryItemId);
        // GB lines may pin a specific lot — only meaningful when the line is
        // actually GB. Strip stale lot pointers if the line type changed.
        const lotId = invItem?.itemType === 'green_bean' ? (item.lotId || '') : '';
        return {
          inventoryItemId: item.inventoryItemId,
          // Carry roastType through so the service layer can split the composite
          // synthetic RB id back into (item_id, roast_type) for the backend.
          roastType: item.roastType || '',
          lotId,
          name: invItem ? invItem.name : item.name || t('purchaseOrder.form.values.unknownItem'),
          description: item.description || invItem?.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          amount: (item.quantity || 0) * (item.unitPrice || 0)
        };
      });
      const shippingValue = data.shippingAmount || 0;

      const customerNameNormalized = (data.customer || '').trim().toLowerCase();
      const existingCustomer = customers.find(c => c.name.trim().toLowerCase() === customerNameNormalized);
      if (existingCustomer) {
        const nextBillingAddress = (data.billingAddress || '').trim();
        const nextShippingAddress = ((data.shippingAddress || '').trim() || nextBillingAddress);
        const nextMessageOnInvoice = (data.messageOnInvoice || '').trim();

        // The SO form's "Message on invoice" is the customer's message: editing
        // it here writes through to the customers table so future orders inherit
        // the new default. Only push when it actually changed.
        const addressChanged =
          !!nextBillingAddress &&
          (nextBillingAddress !== (existingCustomer.billingAddress || '') ||
            nextShippingAddress !== (existingCustomer.shippingAddress || ''));
        const messageChanged = nextMessageOnInvoice !== (existingCustomer.messageOnInvoice || '');

        if (addressChanged || messageChanged) {
          // Preserve whichever fields the form left untouched so a message-only
          // edit doesn't wipe the saved address, and vice-versa.
          const finalBillingAddress = addressChanged ? nextBillingAddress : (existingCustomer.billingAddress || '');
          const finalShippingAddress = addressChanged ? nextShippingAddress : (existingCustomer.shippingAddress || '');

          await customerService.update(existingCustomer.id, {
            name: existingCustomer.name,
            email: existingCustomer.email,
            phone: existingCustomer.phone,
            billingAddress: finalBillingAddress,
            shippingAddress: finalShippingAddress,
            messageOnInvoice: nextMessageOnInvoice,
            internalNotes: existingCustomer.internalNotes
          });
          setCustomers((prev) =>
            prev.map((c) =>
              c.id === existingCustomer.id
                ? { ...c, billingAddress: finalBillingAddress, shippingAddress: finalShippingAddress, messageOnInvoice: nextMessageOnInvoice }
                : c
            )
          );
        }
      }

      await onSubmit({ ...data, shippingAmount: shippingValue, items: enrichedItems });

      if (isInitialDelivered && data.status === 'Cancelled') {
        const orderNumber = initialData && 'orderNumber' in initialData ? (initialData as SalesOrder).orderNumber : 'Unknown';
        const totalAmount = (enrichedItems.reduce((sum, item) => sum + item.amount, 0) + shippingValue);
        
        console.log('--- Financial Transaction Reversal ---');
        console.table([{
          Date: new Date().toLocaleDateString(),
          Type: 'Sales Order',
          Reference: `Cancelled - ${orderNumber}`,
          Amount: -totalAmount
        }]);
      }

      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onCancel();
      }, 1000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsError(true);
      setTimeout(() => setIsError(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (data: SalesOrderFormData) => {
    if (hasQuantityLimitError || isShippingAddressMissing) {
      return;
    }

    const invalidUnitPriceIndexes = data.items.reduce<number[]>((acc, item, index) => {
      if (item.inventoryItemId && Number(item.unitPrice || 0) <= 0) {
        acc.push(index);
      }
      return acc;
    }, []);

    if (invalidUnitPriceIndexes.length > 0) {
      invalidUnitPriceIndexes.forEach((index) => {
        setError(`items.${index}.unitPrice`, {
          type: 'manual',
          message: t('salesOrder.items.unitPriceGreaterThanZero'),
        });
      });
      return;
    }

    if (isInitialDelivered && data.status === 'Cancelled') {
      setPendingSubmitData(data);
      setIsCancelWarningOpen(true);
      return;
    }
    await processSubmit(data);
  };

  return {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    errors,
    isSubmitting,
    fields,
    append,
    remove,
    inventoryItems,
    customers,
    customerOptions,
    loading,
    isDataLoading,
    isSuccess,
    isError,
    isCancelWarningOpen,
    setIsCancelWarningOpen,
    pendingSubmitData,
    setPendingSubmitData,
    isInitialDelivered,
    isInitialCancelled,
    areFieldsDisabled,
    isStatusDisabled,
    isPaymentStatusDisabled,
    restrictStatusOptions,
    subtotalAmount,
    discountType,
    discountValue,
    discountAmount,
    shippingAmount,
    totalAmount,
    balanceDue,
    paymentStatus,
    watchedItems,
    currencySymbol,
    company,
    user,
    processSubmit,
    handleFormSubmit,
    initialData,
    isCreateCustomerModalOpen,
    createCustomerInitialName,
    openCreateCustomerModal,
    closeCreateCustomerModal,
    handleCreateCustomer,
    quantityLimitErrors,
    hasQuantityLimitError,
    hasInvalidUnitPriceError,
    isShippingAddressMissing,
    displayUnit,
    setDisplayUnit,
    ownReservedByItem,
    gbLotsByItem,
  };
};
